import {IntlShape, useIntl} from 'react-intl';
import {PROJECT_STATUS} from '../../../../constants';
import {notNullishAndPredicate} from '../../util/NotNullPredicate';
import {ProjectDropdown_viewer$data} from './__generated__/ProjectDropdown_viewer.graphql';
import {customStageGroupsOf, profileSectionLabel, stageGroupKeyOf, stageLabel} from '../../util/projectStageUtil';
import {getProjectStages} from '../../util/ProjectStageCache';

type ProjectEdges = NonNullable<NonNullable<ProjectDropdown_viewer$data['projects']>['edges']>;
type ProjectNode = NonNullable<NonNullable<ProjectEdges[0]>['node']>;
type ProjectGroupEdges = NonNullable<NonNullable<ProjectDropdown_viewer$data['projectGroups']>['edges']>;
type ProjectGroupNode = NonNullable<NonNullable<ProjectGroupEdges[0]>['node']>;

type ProjectDropdownItem_projectGroup = {
	color: string;
	name: string;
	label: string;
	searchString: string;
	companyProjectGroupId: number;
	isProjectGroup: boolean;
	sortValue: string;
};

type ProjectDropdownItem_project = {
	id: string;
	projectColor: string;
	name: string;
	label: string;
	searchString: string;
	companyProjectId: number;
	customProjectId: string;
	projectGroupId?: string;
	isInProjectGroup?: boolean;
	sortValue: string | number;
};

export type ProjectDropdownItem = ProjectDropdownItem_project | ProjectDropdownItem_projectGroup;

type SortableDropdownItem = {sortValue: string | number; isInProjectGroup?: boolean};

type ProjectOption = {
	value: string;
	label: string;
	nestedOptions: ProjectDropdownItem[];
};

type ProjectDropdownData = {
	selectedProjectLabel: string;
	projectOptions: ProjectOption[];
};

// Replace with relay type once task modal is ported to TS. Should be defined as a ProjectDropdown_project fragment as well.
export type SelectedProject = {
	name?: string;
	client?: {name?: string};
	companyProjectId?: number;
	customProjectId?: string;
	projectColor?: string;
	isProjectGroup?: boolean;
};

// In order to ensure that the connected project parent is always shown when a connected project child is shown, the searchstring for a connected project contains the concatenation of all searchstrings from its children.
function getSearchStringForProjectGroup(
	projectGroupName: string | null,
	companyProjectGroupId: number | null,
	projectGroupProjects: ProjectDropdownItem[]
) {
	const projectGroupProjectsSearchStrings = projectGroupProjects.map(project => project.searchString).join(' ');
	return `${projectGroupName ?? ''} X${companyProjectGroupId ?? ''} ${projectGroupProjectsSearchStrings}`;
}

function projectNodeToProjectDropdownItem(project: ProjectNode, sortValue?: string): ProjectDropdownItem_project {
	return {
		id: project.id,
		projectColor: project.projectColor ?? '',
		name: project.name ?? '',
		label: project.name ?? '',
		searchString: `${project.name ?? ''} ${project.customProjectId ?? ''}`,
		companyProjectId: project.companyProjectId ?? -1,
		customProjectId: project.customProjectId ?? '',
		projectGroupId: project.projectGroupId ?? undefined,
		isInProjectGroup: !!project.projectGroupId,
		sortValue: sortValue ?? project.companyProjectId ?? -1,
	};
}

function projectGroupNodeToProjectDropdownItem(
	projectGroup: ProjectGroupNode,
	searchString: string
): ProjectDropdownItem_projectGroup {
	return {
		color: projectGroup.color ?? '',
		name: projectGroup.name ?? '',
		label: projectGroup.name ?? '',
		searchString,
		companyProjectGroupId: projectGroup.companyProjectGroupId ?? -1,
		isProjectGroup: true,
		sortValue: projectGroup.companyProjectGroupId + '_group',
	};
}

function getProjectItemsForStatuses(
	projects: ProjectEdges,
	statuses: string[],
	externalSortValue?: string
): ProjectDropdownItem[] {
	return projects
		.map(project => project?.node)
		.filter(notNullishAndPredicate(project => statuses.includes(stageGroupKeyOf(project) ?? '')))
		.map(project => projectNodeToProjectDropdownItem(project, externalSortValue));
}

function getProjectGroupNodeItemsForStatuses(projectGroupNode: ProjectGroupNode, statuses: string[]): ProjectDropdownItem[] {
	const projectGroupProjectItemsForStatus = projectGroupNode.projects?.edges
		? getProjectItemsForStatuses(
				projectGroupNode.projects.edges,
				statuses,
				projectGroupNode.companyProjectGroupId + '_group'
		  )
		: [];

	if (projectGroupProjectItemsForStatus.length === 0) return [];

	const searchString = getSearchStringForProjectGroup(
		projectGroupNode.name,
		projectGroupNode.companyProjectGroupId,
		projectGroupProjectItemsForStatus
	);
	const projectGroupItem = projectGroupNodeToProjectDropdownItem(projectGroupNode, searchString);

	return [projectGroupItem, ...projectGroupProjectItemsForStatus];
}

function getProjectGroupItemsForStatuses(projectGroups: ProjectGroupEdges, statuses: string[]): ProjectDropdownItem[] {
	return projectGroups
		.map(projectGroup => projectGroup?.node)
		.filter(notNullishAndPredicate())
		.flatMap(projectGroup => getProjectGroupNodeItemsForStatuses(projectGroup, statuses));
}

function getDropdownItemsForStatuses(
	projects: ProjectEdges,
	projectGroups: ProjectGroupEdges,
	statuses: string[]
): ProjectDropdownItem[] {
	const projectItemsForStatus = getProjectItemsForStatuses(projects, statuses);
	const projectGroupItemsForStatus = getProjectGroupItemsForStatuses(projectGroups, statuses);

	return [...projectItemsForStatus, ...projectGroupItemsForStatus];
}

function getProjectOptions(viewer: ProjectDropdown_viewer$data, hasOpportunityAccess: boolean, intl: IntlShape) {
	const projects = viewer.projects?.edges ?? [];
	const projectGroups = viewer.projectGroups?.edges ?? [];

	const runningProjects = getDropdownItemsForStatuses(projects, projectGroups, [PROJECT_STATUS.RUNNING]);
	const planningProjects = getDropdownItemsForStatuses(projects, projectGroups, [PROJECT_STATUS.PLANNING]);
	const opportunityProjects = getDropdownItemsForStatuses(projects, projectGroups, [PROJECT_STATUS.OPPORTUNITY]);

	const allProjects = [
		...projects.map(project => project?.node),
		...projectGroups.flatMap(group => group?.node?.projects?.edges?.map(edge => edge?.node) ?? []),
	].filter(notNullishAndPredicate());

	const sortProjects = (a: SortableDropdownItem, b: SortableDropdownItem): number => {
		if (a.sortValue < b.sortValue) return -1;
		if (a.sortValue > b.sortValue) return 1;
		if (a.sortValue === b.sortValue) {
			if (a.isInProjectGroup && !b.isInProjectGroup) {
				return 1;
			}
		}
		return 0;
	};
	runningProjects.sort(sortProjects);
	planningProjects.sort(sortProjects);
	opportunityProjects.sort(sortProjects);

	const projectOptions = [
		{
			value: 'running_projects',
			label: profileSectionLabel('RUNNING', intl),
			nestedOptions: runningProjects,
		},
		{
			value: 'planning_projects',
			label: profileSectionLabel('PLANNING', intl),
			nestedOptions: planningProjects,
		},
	];

	// Without opportunity access the group still shows any projects in Opportunity, so they stay reachable.
	if (hasOpportunityAccess || opportunityProjects.length > 0) {
		projectOptions.push({
			value: 'opportunity_projects',
			label: profileSectionLabel('OPPORTUNITY', intl),
			nestedOptions: opportunityProjects,
		});
	}

	// A later stage with a profile is offered only where that profile's projects are: Running, Planning and Opportunity,
	// whose access-or-projects rule a stage with projects always meets. A stage without a profile always is.
	const offeredProfiles: ReadonlyArray<string> = [
		PROJECT_STATUS.RUNNING,
		PROJECT_STATUS.PLANNING,
		PROJECT_STATUS.OPPORTUNITY,
	];
	customStageGroupsOf(allProjects, getProjectStages()).forEach(({key, stage}) => {
		if (stage.profile && !offeredProfiles.includes(stage.profile)) {
			return;
		}
		const nestedOptions = getDropdownItemsForStatuses(projects, projectGroups, [key]).sort(sortProjects);
		if (nestedOptions.length > 0) {
			projectOptions.push({value: key, label: stageLabel(stage, intl), nestedOptions});
		}
	});

	return projectOptions;
}

export const useProjectDropdownController = (
	viewer: ProjectDropdown_viewer$data,
	hasOpportunityAccess: boolean, // Supplied as a prop since it's loaded from session storage, meaning it is hard to mock for testing
	selectedProject?: SelectedProject
): ProjectDropdownData => {
	const intl = useIntl();

	const projectOptions = getProjectOptions(viewer, hasOpportunityAccess, intl);
	const selectedProjectLabel = selectedProject?.client?.name ?? intl.formatMessage({id: 'common.project'});
	return {selectedProjectLabel, projectOptions};
};
